// Accounting › Bills to pay (Blueprint's; pages/ux-full accounting/payables), its own module out of
// Books.tsx, on ui-web's ListPage (patterns/README.md, BL-189). The title block's figures are the aging, and
// its actions enter a bill or a vendor's credit note (opened over the list). The list is To approve: each
// bill checked three ways against its PO and what was received; approving it posts it. Under it, the
// receipts for parts bought on jobs, what's approved and unpaid (Pay opens its form under the row), the
// vendor credits to use, the returns awaiting credit, what's on hold, the purchase orders over the limit
// waiting for the owner, and the vendors (PayablesParts.tsx). Riverstone keeps books/Payables.tsx.
// payables-page.css lays out the parts, tokens only.
import { Button, Figures, Link, RowList, SelectField, TextField, type Row } from '@flowology/ui-web/fl';
import { ListPage, Section, type ListStatus } from '@flowology/ui-web/patterns';
import { useState, type ReactNode } from 'react';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { holdsAny } from '../shared/may.ts';
import { newId } from '../shared/ids.ts';
import { AP_PERMS, BUCKETS, BUCKET_LABEL, dayText, money, todayIn } from './model.ts';
import { BOOKS_PARTS } from './parts.ts';
import {
  Acts,
  BillEntry,
  ReceiptsSection,
  ReturnsSection,
  VendorCreditsSection,
  VendorsSection,
  useClosedHint,
} from './PayablesParts.tsx';
import type { ApAging, BankAccount, PurchaseOrder, VendorBill } from './types.ts';
import { useAct, useQuery, type Act } from './ui.tsx';
import './payables-page.css';

const PART = BOOKS_PARTS.find((p) => p.part === 'payables')!;
/** Who orders (purchasing.order): the owner, the manager and the warehouse. */
const PO_PERMS = ['purchasing.order'];
const METHODS = [
  { value: 'eft', label: 'EFT' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'pad', label: 'Pre-authorised debit' },
  { value: 'etransfer', label: 'E-transfer' },
  { value: 'card', label: 'Card' },
];
type Method = 'eft' | 'cheque' | 'card' | 'pad' | 'etransfer';
type Bill = ApAging['rows'][number];
/** A region's state from what's read: its rows (none is empty), why it didn't load, or still loading. */
const stateOf = (rows: readonly unknown[] | null | undefined, error: string | null): ListStatus =>
  rows ? (rows.length ? 'ready' : 'empty') : error ? 'failed' : 'loading';

/** Paying one bill, under its row: from which bank, how, when, and its reference. */
function PayForm({
  bill,
  banks,
  act,
  timeZone,
  onDone,
}: {
  bill: Bill;
  banks: BankAccount[];
  act: Act;
  timeZone?: string;
  onDone: () => void;
}) {
  const [bankAccountId, setBank] = useState(banks[0]?.id ?? '');
  const [method, setMethod] = useState<Method>('eft');
  const [paidOn, setPaidOn] = useState(todayIn(timeZone));
  const [reference, setReference] = useState('');
  const hint = useClosedHint();
  return (
    <div className="bp-ap-form">
      <SelectField
        label="From"
        value={bankAccountId}
        onChange={setBank}
        options={banks.map((b) => ({ value: b.id, label: b.name }))}
      />
      <SelectField label="How" value={method} onChange={(m) => setMethod(m as Method)} options={METHODS} />
      <TextField
        label="Paid on"
        type="date"
        value={paidOn}
        onChange={setPaidOn}
        {...(hint(paidOn) ? { description: hint(paidOn) } : {})}
      />
      <TextField label="Reference" value={reference} onChange={setReference} placeholder="Optional" />
      <Acts>
        <Button
          variant="primary"
          isDisabled={act.busy || !bankAccountId}
          onPress={() =>
            void (async () => {
              const ok = await act.run(
                'vendorBills.pay',
                {
                  paymentId: newId(),
                  vendorId: bill.vendorId,
                  bankAccountId,
                  paidOn,
                  method,
                  ...(reference ? { reference } : {}),
                  allocations: [{ billId: bill.billId, amountCents: bill.owingCents }],
                },
                'Paid and posted.',
              );
              if (ok) onDone();
            })()
          }
        >
          Pay {money(bill.owingCents)}
        </Button>
        <Link onPress={onDone}>Cancel</Link>
      </Acts>
    </div>
  );
}

/** A bill waiting for approval: matched three ways when it has a PO; Approve posts it, Hold keeps it. */
function draftRow(b: VendorBill, act: Act): Row {
  const variance = b.match.some((m) => m.priceVarianceCents !== 0);
  const short = b.match.some((m) => m.heldQty !== '0' && m.heldQty !== '0.000');
  return {
    id: b.id,
    name: b.number,
    label: `${b.number} · ${b.vendorName}`,
    after: { text: ` ${b.vendorName}` },
    lines: [
      { text: `${b.vendorRef} · billed ${dayText(b.billDate)} · due ${dayText(b.dueDate)}` },
      ...(b.poId
        ? [
            short
              ? { word: 'Not all received yet: that part waits', tone: 'wait' as const }
              : variance
                ? { word: 'Price differs from the PO', tone: 'wait' as const }
                : { word: '3-way match', tone: 'muted' as const },
          ]
        : []),
    ],
    end: { figure: money(b.totalCents) },
    actions: [
      {
        id: 'approve',
        text: variance ? 'Approve with the price difference' : 'Approve',
        isDisabled: act.busy,
        onPress: () =>
          void (b.kind === 'credit'
            ? act.run('vendorBills.approveCredit', { creditId: b.id }, 'Approved.')
            : act.run(
                'vendorBills.approve',
                { billId: b.id, acceptPriceVariance: variance },
                'Approved and posted.',
              )),
      },
      {
        id: 'hold',
        text: 'Hold',
        isDisabled: act.busy,
        onPress: () =>
          void act.run(
            'vendorBills.setHold',
            { billId: b.id, on: true, reason: 'Held from the books' },
            'On hold.',
          ),
      },
    ],
  };
}

export function PayablesPage({
  nav,
  held: may,
  toast,
  timeZone,
}: {
  nav: NavProps;
  held: readonly string[];
  toast: (m: string) => void;
  timeZone?: string;
}) {
  const aging = useQuery<ApAging>('payables.aging', {});
  const drafts = useQuery<{ bills: VendorBill[] }>('vendorBills.list', { status: 'draft' });
  const held = useQuery<{ bills: VendorBill[] }>('vendorBills.list', { status: 'on_hold' });
  const banks = useQuery<{ bankAccounts: BankAccount[] }>('bankAccounts.list', {});
  // Purchase orders are for those who order (purchasing.order): asked only of them (QA-260).
  const ordersPos = holdsAny(may, PO_PERMS);
  const pos = useQuery<{ orders: PurchaseOrder[] }>('purchaseOrders.list', ordersPos ? {} : null);
  // Entering bills and vendors (gap #9): a new one reloads the lists it lands in, and an approval
  // or a payment reloads the vendor credits too (version).
  const [version, setVersion] = useState(0);
  const act = useAct(toast, async () => {
    setVersion((v) => v + 1);
    await Promise.all([aging.reload(), drafts.reload(), held.reload(), pos.reload()]);
  });
  const [paying, setPaying] = useState<string | null>(null);
  const [entering, setEntering] = useState<'bill' | 'credit' | null>(null);
  // approving a purchase order (purchaseOrders.approve): purchasing.approve, the owner's or a role of theirs
  const isOwner = may.includes('purchasing.approve');
  const enters = holdsAny(may, AP_PERMS);
  const changed = () => {
    setVersion((v) => v + 1);
    void Promise.all([aging.reload(), drafts.reload()]);
  };
  const crumb = usePageCrumb(nav);
  const head = {
    ...(crumb ? { crumb } : {}),
    title: PART.bp ?? PART.rail,
    lede: PART.lede,
    ...(enters && !entering
      ? {
          actions: (
            <>
              <Button variant="primary" onPress={() => setEntering('bill')}>
                Enter a bill
              </Button>
              <Link onPress={() => setEntering('credit')}>Enter a vendor credit</Link>
            </>
          ),
        }
      : {}),
    ...(aging.data
      ? {
          figures: (
            <Figures
              items={BUCKETS.map((b) => ({ label: BUCKET_LABEL[b], value: money(aging.data!.buckets[b]) }))}
            />
          ),
        }
      : {}),
  };
  const frame = (body: ReactNode) => (
    <div className="app">
      <main>
        <PageBanners nav={nav} />
        {body}
      </main>
    </div>
  );
  if (!aging.data)
    return frame(
      <ListPage
        head={head}
        list={{
          title: 'To approve',
          what: 'the bills',
          status: aging.error ? 'failed' : 'loading',
          failedText: aging.error ?? 'The bills didn’t load.',
          onRetry: () => void aging.reload(),
        }}
      />,
    );
  const waitingPos = (pos.data?.orders ?? []).filter((o) => o.needsOwnerApproval && o.status === 'draft');
  const toPay: Row[] = aging.data.rows.map((r) => ({
    id: r.billId,
    name: r.number,
    label: `${r.number} · ${r.vendorName}`,
    ...(r.daysPastDue > 0
      ? {
          word: {
            text: `${r.daysPastDue} day${r.daysPastDue === 1 ? '' : 's'} overdue`,
            tone: 'bad' as const,
          },
        }
      : {}),
    after: { text: ` ${r.vendorName}` },
    lines: [
      { text: `Due ${dayText(r.dueDate)}` },
      ...(paying === r.billId && banks.data
        ? [
            {
              part: (
                <PayForm
                  bill={r}
                  banks={banks.data.bankAccounts}
                  act={act}
                  {...(timeZone ? { timeZone } : {})}
                  onDone={() => setPaying(null)}
                />
              ),
            },
          ]
        : []),
    ],
    end: { figure: money(r.owingCents) },
    actions: [{ id: 'pay', text: 'Pay', onPress: () => setPaying(r.billId) }],
  }));
  const onHold: Row[] = (held.data?.bills ?? []).map((b) => ({
    id: b.id,
    name: b.number,
    label: `${b.number} · ${b.vendorName}`,
    after: { text: ` ${b.vendorName}` },
    lines: [{ text: b.holdReason ?? 'On hold' }],
    end: { figure: money(b.totalCents) },
    actions: [
      {
        id: 'release',
        text: 'Release',
        isDisabled: act.busy,
        onPress: () => void act.run('vendorBills.setHold', { billId: b.id, on: false }, 'Released.'),
      },
    ],
  }));
  const orders: Row[] = waitingPos.map((o) => ({
    id: o.id,
    name: o.number,
    label: o.number,
    lines: [
      { text: o.auto ? 'Drafted by the weekly reorder' : 'Entered by the warehouse' },
      ...(isOwner ? [] : [{ text: 'Waiting for the owner' }]),
    ],
    end: { figure: money(o.totalCents) },
    ...(isOwner
      ? {
          actions: [
            {
              id: 'approve',
              text: 'Approve',
              isDisabled: act.busy,
              onPress: () =>
                void act.run(
                  'purchaseOrders.approve',
                  { poId: o.id },
                  'Approved: the warehouse can send it.',
                ),
            },
          ],
        }
      : {}),
  }));
  return frame(
    <ListPage
      head={head}
      list={{
        title: 'To approve',
        ...(drafts.data ? { count: drafts.data.bills.length } : {}),
        what: 'the bills to approve',
        status: stateOf(drafts.data?.bills, drafts.error),
        emptyText: 'No bill is waiting for approval.',
        failedText: drafts.error ?? 'The bills to approve didn’t load.',
        onRetry: () => void drafts.reload(),
        ...(entering
          ? {
              before: (
                <BillEntry
                  toast={toast}
                  {...(timeZone ? { timeZone } : {})}
                  credit={entering === 'credit'}
                  onClose={() => setEntering(null)}
                  onChanged={changed}
                />
              ),
            }
          : {}),
        children: drafts.data ? (
          <RowList label="To approve" rows={drafts.data.bills.map((b) => draftRow(b, act))} />
        ) : null,
      }}
      below={
        <div className="bp-ap">
          {/* parts techs bought on jobs (BL-012): approved into AP or onto a company card */}
          {enters && <ReceiptsSection toast={toast} onChanged={changed} />}
          <Section title="To pay" count={aging.data.rows.length}>
            <RowList label="To pay" rows={toPay} empty="Nothing approved is unpaid." />
          </Section>
          {enters && <VendorCreditsSection toast={toast} version={version} onChanged={changed} />}
          {/* parts sent back to vendors (BL-013): their credit entered from the return, or the return closed */}
          {enters && (
            <ReturnsSection
              toast={toast}
              {...(timeZone ? { timeZone } : {})}
              version={version}
              onChanged={changed}
            />
          )}
          {onHold.length > 0 && (
            <Section title="On hold">
              <RowList label="On hold" rows={onHold} />
            </Section>
          )}
          {ordersPos && (
            <Section title="Purchase orders over the limit" count={waitingPos.length}>
              <RowList
                label="Purchase orders over the limit"
                rows={orders}
                empty="No purchase order is waiting for the owner."
              />
            </Section>
          )}
          {enters && <VendorsSection toast={toast} onChanged={changed} />}
        </div>
      }
    />,
  );
}
